import { FormType } from '../../interfaces/form.interface';
import type { ReactiveFormLoadPlan } from '../load/reactive-form-load-orchestrator';

export type ReactiveFormHostMode = 'new' | 'edit' | 'duplicate' | 'read' | '';

export type ReactiveFormHostLifecycleEvent =
    'modeResolved' |
    'modelChanged' |
    'previousDataChanged' |
    'minimumReadyTimeElapsed' |
    'externalDropdownsReset' |
    'externalDropdownsReady' |
    'loadPlanResolved' |
    'loadShellReady' |
    'loadRecordReady' |
    'loadBlockingErrorChanged' |
    'formReady' |
    'repeatablesReady' |
    'stableValueChanged' |
    'navigationResolved' |
    'undoStateResolved' |
    'requestButtonTypeResolved' |
    'destroyed';

export interface ReactiveFormHostReadinessState {
    readonly formReady: boolean;
    readonly dropdownsReady: boolean;
    readonly repeatablesReady: boolean;
    readonly externalDropdownsReady: boolean;
    readonly externalDropdownsReadyPending: boolean;
    readonly minTimeExhausted: boolean;
    readonly loadShellReady: boolean;
    readonly loadRecordReady: boolean;
    readonly loadBlockingErrors: boolean;
}

export interface ReactiveFormHostStateSnapshot<TModel> extends ReactiveFormHostReadinessState {
    readonly mode: ReactiveFormHostMode;
    readonly formType: FormType | null;
    readonly model: TModel;
    readonly modelChanges: TModel;
    readonly previousData: TModel | null;
    readonly modelOld: Readonly<Record<string, string>>;
    readonly pathToGoBack: string;
    readonly undoEnabled: boolean;
    readonly requestButtonType: number;
    readonly loadPlan: ReactiveFormLoadPlan | null;
    readonly lifecycleEvents: readonly ReactiveFormHostLifecycleEvent[];
}

export interface ReactiveFormHostStateInitialValue<TModel> {
    readonly mode?: ReactiveFormHostMode | undefined;
    readonly formType?: FormType | null | undefined;
    readonly model?: TModel | undefined;
    readonly modelChanges?: TModel | undefined;
    readonly previousData?: TModel | null | undefined;
    readonly modelOld?: Record<string, string> | undefined;
    readonly pathToGoBack?: string | undefined;
    readonly undoEnabled?: boolean | undefined;
    readonly requestButtonType?: number | undefined;
}

const defaultMode: ReactiveFormHostMode = '';
const defaultRequestButtonType = 0;

/** Stores common reactive form host state without knowing application routing or submit details. */
export class ReactiveFormHostState<TModel> {
    private lifecycleEvents: ReactiveFormHostLifecycleEvent[] = [];

    public mode: ReactiveFormHostMode;
    public formType: FormType | null;
    public model: TModel;
    public modelChanges: TModel;
    public previousData: TModel | null;
    public modelOld: Record<string, string>;
    public pathToGoBack: string;
    public undoEnabled: boolean;
    public requestButtonType: number;
    public formReady = false;
    public dropdownsReady = false;
    public repeatablesReady = false;
    public externalDropdownsReady = false;
    public externalDropdownsReadyPending = false;
    public minTimeExhausted = false;
    public loadPlan: ReactiveFormLoadPlan | null = null;
    public loadShellReady = false;
    public loadRecordReady = false;
    public loadBlockingErrors = false;

    public constructor(initialValue: ReactiveFormHostStateInitialValue<TModel> = {}) {
        this.mode = initialValue.mode ?? defaultMode;
        this.formType = initialValue.formType ?? null;
        this.model = initialValue.model ?? ({} as TModel);
        this.modelChanges = initialValue.modelChanges ?? ({} as TModel);
        this.previousData = initialValue.previousData ?? null;
        this.modelOld = initialValue.modelOld ?? {};
        this.pathToGoBack = initialValue.pathToGoBack ?? '';
        this.undoEnabled = initialValue.undoEnabled ?? false;
        this.requestButtonType = initialValue.requestButtonType ?? defaultRequestButtonType;
    }

    public setMode(mode: ReactiveFormHostMode): void {
        this.mode = mode;
        this.formType = ReactiveFormHostState.resolveFormType(mode);
        this.track('modeResolved');
    }

    public setModeFromFormType(formType: FormType): void {
        this.formType = formType;
        this.mode = ReactiveFormHostState.resolveMode(formType);
        this.track('modeResolved');
    }

    public setModel(model: TModel | null | undefined): void {
        this.model = model ?? ({} as TModel);
        this.previousData = this.model;
        this.track('modelChanged');
    }

    public setPreviousData(previousData: TModel | null | undefined): void {
        this.previousData = previousData ?? null;
        this.track('previousDataChanged');
    }

    public setModelChanges(modelChanges: TModel | null | undefined): void {
        this.modelChanges = modelChanges ?? ({} as TModel);
    }

    public resetExternalDropdowns(): void {
        this.externalDropdownsReady = false;
        this.externalDropdownsReadyPending = false;
        this.dropdownsReady = false;
        this.track('externalDropdownsReset');
    }

    public setLoadPlan(loadPlan: ReactiveFormLoadPlan | null | undefined): void {
        this.loadPlan = loadPlan ?? null;
        this.loadShellReady = false;
        this.loadRecordReady = false;
        this.loadBlockingErrors = false;
        this.track('loadPlanResolved');
    }

    public markLoadShellReady(): void {
        this.loadShellReady = true;
        this.track('loadShellReady');
    }

    public markLoadRecordReady(): void {
        this.loadRecordReady = true;
        this.track('loadRecordReady');
    }

    public setLoadBlockingErrors(loadBlockingErrors: boolean): void {
        this.loadBlockingErrors = loadBlockingErrors;
        this.track('loadBlockingErrorChanged');
    }

    public finalizeExternalDropdowns(): void {
        this.externalDropdownsReady = true;
        this.externalDropdownsReadyPending = true;
        this.dropdownsReady = true;
        this.track('externalDropdownsReady');
    }

    public consumeExternalDropdownsReadyPending(): boolean {
        if (!this.externalDropdownsReadyPending) {
            return false;
        }

        this.externalDropdownsReadyPending = false;
        return true;
    }

    public markMinimumReadyTimeElapsed(): void {
        this.minTimeExhausted = true;
        this.track('minimumReadyTimeElapsed');
    }

    public markFormReady(): void {
        this.formReady = true;
        this.track('formReady');
    }

    public resetFormReady(): void {
        this.formReady = false;
    }

    public markRepeatablesReady(): void {
        this.repeatablesReady = true;
        this.track('repeatablesReady');
    }

    public resetRepeatablesReady(): void {
        this.repeatablesReady = false;
    }

    public markStableValueChanged(): void {
        this.track('stableValueChanged');
    }

    public setNavigationPath(pathToGoBack: string | null | undefined): void {
        this.pathToGoBack = pathToGoBack ?? '';
        this.track('navigationResolved');
    }

    public setUndoEnabled(undoEnabled: boolean): void {
        this.undoEnabled = undoEnabled;
        this.track('undoStateResolved');
    }

    public setRequestButtonType(requestButtonType: number): void {
        this.requestButtonType = requestButtonType;
        this.track('requestButtonTypeResolved');
    }

    public markDestroyed(): void {
        this.track('destroyed');
    }

    public snapshot(): ReactiveFormHostStateSnapshot<TModel> {
        return {
            mode: this.mode,
            formType: this.formType,
            model: this.model,
            modelChanges: this.modelChanges,
            previousData: this.previousData,
            modelOld: { ...this.modelOld },
            pathToGoBack: this.pathToGoBack,
            undoEnabled: this.undoEnabled,
            requestButtonType: this.requestButtonType,
            loadPlan: this.loadPlan,
            formReady: this.formReady,
            dropdownsReady: this.dropdownsReady,
            repeatablesReady: this.repeatablesReady,
            externalDropdownsReady: this.externalDropdownsReady,
            externalDropdownsReadyPending: this.externalDropdownsReadyPending,
            minTimeExhausted: this.minTimeExhausted,
            loadShellReady: this.loadShellReady,
            loadRecordReady: this.loadRecordReady,
            loadBlockingErrors: this.loadBlockingErrors,
            lifecycleEvents: [...this.lifecycleEvents],
        };
    }

    public static resolveMode(formType: FormType | null | undefined): ReactiveFormHostMode {
        switch (formType) {
        case FormType.CREATION:
            return 'new';
        case FormType.EDITION:
            return 'edit';
        case FormType.DUPLICATION:
            return 'duplicate';
        case FormType.INFORMATIVE:
            return 'read';
        default:
            return defaultMode;
        }
    }

    public static resolveFormType(mode: ReactiveFormHostMode): FormType | null {
        switch (mode) {
        case 'new':
            return FormType.CREATION;
        case 'edit':
            return FormType.EDITION;
        case 'duplicate':
            return FormType.DUPLICATION;
        case 'read':
            return FormType.INFORMATIVE;
        default:
            return null;
        }
    }

    private track(event: ReactiveFormHostLifecycleEvent): void {
        this.lifecycleEvents = [...this.lifecycleEvents, event];
    }
}
